export interface LeadOverviewSection {
  id: string;
  title: string;
  icon: string;
  expandedByDefault?: boolean;
  fields: any[];
  fixedFields?: {
    key: string;
    label: string;
    type?: string;
    required?: boolean;
    bind: 'root' | 'fields';
    /** Resolves options in overview via getFixedFieldOptions (lead_source, lead_type, etc.). */
    optionsKey?: string;
    readOnly?: boolean;
  }[];
}

const SECTION_DEFS: Omit<LeadOverviewSection, 'fields'>[] = [
  {
    id: 'bio',
    title: 'Bio Details',
    icon: 'fa-user-circle',
    expandedByDefault: true,
  },
  {
    id: 'assignment',
    title: 'Assignment, Query & Course Details',
    icon: 'fa-graduation-cap',
  },
  { id: 'lead', title: 'Lead Details', icon: 'fa-bolt' },
  { id: 'contact', title: 'Contact Details', icon: 'fa-phone' },
  { id: 'demographic', title: 'Demographic Details', icon: 'fa-map-marker' },
  { id: 'remarks', title: 'Remarks', icon: 'fa-comment' },
  { id: 'other', title: 'Other Details', icon: 'fa-th-large' },
];

function norm(s: string): string {
  return (s || '').toLowerCase().replace(/[\s_-]+/g, '');
}

function matchesSection(field: any, sectionId: string): boolean {
  const name = norm(field.field_name || '');
  const label = norm(field.label || '');

  const rules: Record<string, RegExp[]> = {
    bio: [
      /^prefix$/,
      /first.?name/,
      /last.?name/,
      /fname/,
      /lname/,
      /dateofbirth|dob|birth/,
      /^age$/,
      /gender/,
      /nationality/,
      /interested.?course|course/,
    ],
    assignment: [
      /assign/,
      /query/,
      /course/,
      /program/,
      /department/,
      /campus/,
      /enrollment/,
    ],
    lead: [/leadstatus|leadtype|leadsource|leadscore|pipeline|priority/],
    contact: [
      /contact/,
      /email/,
      /phone|mobile/,
      /company|organisation|organization/,
      /countrycode/,
    ],
    demographic: [
      /address/,
      /city/,
      /state/,
      /country/,
      /pincode|zip|postal/,
      /demographic/,
      /location/,
    ],
    remarks: [/remark|comment|feedback|note|update/],
  };

  const list = rules[sectionId];
  if (!list) {
    return sectionId === 'other';
  }
  return list.some((rx) => rx.test(name) || rx.test(label));
}

function resolveSectionId(field: any, assigned: Set<string>): string {
  for (const def of SECTION_DEFS) {
    if (def.id === 'other') {
      continue;
    }
    if (matchesSection(field, def.id)) {
      return def.id;
    }
  }
  return 'other';
}

function normFieldKey(s: string): string {
  return (s || '').toLowerCase().replace(/[\s_-]+/g, '');
}

const FIXED_FIELD_NAME_KEYS = new Set([
  'leadsource',
  'leadtype',
  'leadstatus',
  'contactname',
  'contactemail',
  'contactphone',
  'companyname',
  'countrycode',
]);

export function buildLeadOverviewSections(
  allLeadFields: any[],
  options: { includeFixedContact?: boolean } = {}
): LeadOverviewSection[] {
  const sections: LeadOverviewSection[] = SECTION_DEFS.map((d) => ({
    ...d,
    fields: [],
    fixedFields: [],
  }));
  const byId = Object.fromEntries(sections.map((s) => [s.id, s]));
  const assigned = new Set<string>();

  (allLeadFields || []).forEach((field) => {
    if (!field?.field_name || assigned.has(field.field_name)) {
      return;
    }
    const sid = field.profile_section || resolveSectionId(field, assigned);
    const section = byId[sid] || byId['other'];
    if (
      options.includeFixedContact &&
      FIXED_FIELD_NAME_KEYS.has(normFieldKey(field.field_name))
    ) {
      assigned.add(field.field_name);
      return;
    }
    section.fields.push(field);
    assigned.add(field.field_name);
  });

  if (options.includeFixedContact) {
    byId['contact'].fixedFields = [
      { key: 'company_name', label: 'Organisation', type: 'text', bind: 'root' },
      { key: 'contact_name', label: 'Contact Name', type: 'text', required: true, bind: 'root' },
      { key: 'contact_email', label: 'Contact Email', type: 'email', bind: 'root' },
      { key: 'contact_phone', label: 'Contact Number', type: 'text', bind: 'root' },
      { key: 'country_code', label: 'Country Code', type: 'text', bind: 'root' },
      {
        key: 'customer_id',
        label: 'Customer ID',
        type: 'text',
        bind: 'root',
        readOnly: true,
      },
    ];
    byId['lead'].fixedFields = [
      { key: 'lead_source', label: 'Lead Source', type: 'select', bind: 'root', optionsKey: 'lead_source' },
      { key: 'lead_type', label: 'Lead Type', type: 'select', bind: 'root', optionsKey: 'lead_type' },
    ];
  }

  return sections.filter(
    (s) => (s.fields?.length ?? 0) > 0 || (s.fixedFields?.length ?? 0) > 0
  );
}
